import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  colors,
  customShadows,
  borderRadiusPx,
} from "../../../../theme";

/**
 * CommonDialog Component
 * @param {Object} props
 * @param {boolean} props.open - Controls open/close state of the dialog
 * @param {Function} props.onClose - Callback fired when close icon or backdrop is clicked
 * @param {React.ReactNode} props.title - Title text or component for the dialog header
 * @param {React.ReactNode} [props.subtitle] - Optional subtitle text beneath the title
 * @param {React.ReactNode} props.children - Dialog body content
 * @param {React.ReactNode} [props.actions] - Footer action buttons (e.g. CommonButton instances)
 * @param {string} [props.maxWidth="xs"] - Max width breakpoint ('xs' | 'sm' | 'md' | 'lg' | 'xl')
 * @param {boolean} [props.fullWidth=true] - Whether dialog should stretch to maxWidth
 * @param {boolean} [props.dividers=true] - Whether DialogContent has top/bottom divider borders
 * @param {boolean} [props.showCloseButton=true] - Whether to show top-right X close icon button
 * @param {Object} [props.contentSx] - Additional SX styling for DialogContent
 * @param {Object} [props.actionsSx] - Additional SX styling for DialogActions
 * @param {Object} [props.titleSx] - Additional SX styling for DialogTitle
 * @param {Object} [props.PaperProps] - Custom overrides for Paper element
 */
const CommonDialog = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  maxWidth = "xs",
  fullWidth = true,
  dividers = true,
  showCloseButton = true,
  contentSx = {},
  actionsSx = {},
  titleSx = {},
  PaperProps = {},
  disableScrollLock = true,
  ...props
}) => {
  return (
    <Dialog
      disableScrollLock={disableScrollLock}
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        ...PaperProps,
        sx: {
          borderRadius: borderRadiusPx.dialog,
          p: 1,
          boxShadow: customShadows.dialog,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          backgroundColor: colors.background.paper,
          ...(PaperProps?.sx || {}),
        },
      }}
      {...props}
    >
      {title ? (
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: subtitle ? "flex-start" : "center",
            pb: 1,
            px: 2.5,
            pt: 2,
            ...titleSx,
          }}
        >
          <Box sx={{ pr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.slate[900],
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography
                variant="body2"
                sx={{ color: colors.slate[500], mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {showCloseButton && onClose ? (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: colors.slate[400],
                "&:hover": {
                  color: colors.slate[700],
                  backgroundColor: colors.slate[100],
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </DialogTitle>
      ) : null}

      <DialogContent
        dividers={dividers}
        onWheel={(event) => {
          event.stopPropagation();
        }}
        sx={{
          py: 2.5,
          px: 2.5,
          borderColor: colors.dashboard.divider,
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {actions ? (
        <DialogActions
          sx={{
            gap: 1,
            ...actionsSx,
            px: { xs: 2.5, sm: 4 },
            py: 2.5,
            borderTop: `1px solid ${colors.dashboard.cardBorder}`,
          }}
        >
          {actions}
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

CommonDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  children: PropTypes.node,
  actions: PropTypes.node,
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
  fullWidth: PropTypes.bool,
  dividers: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  contentSx: PropTypes.object,
  actionsSx: PropTypes.object,
  titleSx: PropTypes.object,
  PaperProps: PropTypes.object,
};

export default CommonDialog;
